import { formatAbn } from "utils/abn";
import CircularProgress from "@mui/material/CircularProgress";
import axios from "axios";
import { Skeleton } from "components/Skeleton";
import { showDraftSavedMessage } from "components/InlineFeedback/DraftSaveConfirmation";
import { ClearableSearchInput } from "components/SearchField/SearchField";
import { useEffect, useMemo, useState } from "react";
import { appConstants } from "utils/appConstants";
import {
  LuArrowLeft,
  LuCheck,
  LuCircleAlert,
  LuChevronLeft,
  LuChevronRight,
  LuCoins,
  LuCreditCard,
  LuFileText,
  LuSearch,
  LuReceiptText,
  LuTriangleAlert,
  LuX,
} from "react-icons/lu";

const PLAN_DATA = {
  Silver: {
    price: 0,
    kicker: "ENTRY PLAN",
    copy: "For freelancers and small projects.",
    features: [
      "10 invoices & quotes a month",
      "Reminder templates & themes",
      "Debt cases: view only",
    ],
  },
  Gold: {
    price: 99,
    kicker: "CURRENT PLAN",
    copy: "For invoicing, reporting and payment recovery.",
    features: [
      "100 invoices, quotes, RCTI, expenses",
      "Reports & reminder logs",
      "Full debt collection actions",
    ],
  },
  Platinum: {
    price: 299,
    kicker: "GROWING TEAMS",
    copy: "Support for complex payment recovery.",
    features: [
      "Unlimited everything",
      "Dedicated recovery specialist",
      "Priority escalation to collectors",
    ],
  },
};

const PLAN_LIMITS = { Silver: 10, Gold: 100, Platinum: null };

const numberOrZero = (value) => {
  const parsed = Number(value);
  return Number.isFinite(parsed) ? parsed : 0;
};

function ActionButton({ children, secondary = false, className = "", ...props }) {
  return (
    <button
      type="button"
      className={`plan-billing-button ${secondary ? "secondary" : "primary"}${
        className ? ` ${className}` : ""
      }`}
      {...props}
    >
      {children}
    </button>
  );
}

function PageHeading({ title, copy, onBack }) {
  return (
    <header className="settings-section-heading plan-billing-heading">
      {onBack && (
        <button type="button" className="plan-billing-back" onClick={onBack}>
          <LuArrowLeft /> Back to Plan &amp; billing
        </button>
      )}
      <h2>{title}</h2>
      <p>{copy}</p>
    </header>
  );
}

function CurrentPlan({ plan, price, invoices, quotes, invoiceLimit, quoteLimit, action }) {
  const limitLabel = (value) => value == null ? "Unlimited" : value;
  return (
    <section className="plan-current-card">
      <div>
        <small>YOUR CURRENT PLAN</small>
        <h3>{plan}</h3>
        <strong>{price ? `$${price} AUD / month` : "Free"}</strong>
        <span>Current subscription</span>
        <p>Invoices {invoices} / {limitLabel(invoiceLimit)}&nbsp; · &nbsp;Quotations {quotes} / {limitLabel(quoteLimit)}</p>
      </div>
      {action}
    </section>
  );
}

function Overview({ data, setView }) {
  const remainingInvoices = data.invoiceLimit == null ? null : Math.max(0, data.invoiceLimit - data.invoices);
  const remainingQuotes = data.quoteLimit == null ? null : Math.max(0, data.quoteLimit - data.quotes);
  const limitLabel = (value) => value == null ? "Unlimited" : value;
  return (
    <>
      <PageHeading
        title="Plan & billing"
        copy="Manage your subscription, track usage and credits, and view your billing details and invoice history."
      />
      <CurrentPlan {...data} />
      <div className="plan-usage-grid">
        <section><LuReceiptText /><span>Invoices</span><strong>{data.invoices} / {limitLabel(data.invoiceLimit)}</strong><p>{remainingInvoices == null ? "Unlimited invoices this month" : `${remainingInvoices} invoices remaining this month`}</p></section>
        <section><LuFileText /><span>Quotations</span><strong>{data.quotes} / {limitLabel(data.quoteLimit)}</strong><p>{remainingQuotes == null ? "Unlimited quotations this month" : `${remainingQuotes} quotations remaining this month`}</p></section>
        <section><LuCoins /><span>Credits</span><strong>{data.credits.toLocaleString()}</strong><p>For SMS, ABN checks and letters of demand</p><button type="button" onClick={() => setView("credits")}>Top up</button></section>
      </div>
      <div className="plan-navigation-grid">
        <section><h3>Invoice history</h3><p>View and download past subscription invoices.</p><button type="button" onClick={() => setView("history")}>View invoice history <LuChevronRight /></button></section>
        <section><h3>Billing details</h3><p>Manage billing contact and payment method.</p><button type="button" onClick={() => setView("billing")}>Manage billing details <LuChevronRight /></button></section>
        <section><h3>Manage plan</h3><p>Review plan features and available options.</p><button type="button" onClick={() => setView("manage")}>Review plans <LuChevronRight /></button></section>
      </div>
    </>
  );
}

function CreditManagement({ data, card, billingData, setBillingData, setView, user }) {
  const [amount, setAmount] = useState("");
  const [search, setSearch] = useState("");
  const [page, setPage] = useState(1);
  const [ledger, setLedger] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [purchasing, setPurchasing] = useState(false);
  const [message, setMessage] = useState("");
  const pageSize = 10;
  const businessUserID = user.businessUserID;
  const unitPrice = numberOrZero(billingData?.creditUnitPrice);
  const credits = Number(amount) || 0;
  const price = credits * unitPrice;
  const hasCreditValue = amount !== "";
  const creditError = hasCreditValue &&
    (!Number.isInteger(Number(amount)) || Number(amount) < 1 || Number(amount) > 2000)
    ? "Enter between 1 and 2,000 credits."
    : "";

  const fetchLedger = async (nextPage = page, term = search) => {
    if (!businessUserID) return;
    setLoading(true);
    try {
      const response = await axios.post(
        `${appConstants.baseURL}billing/fetchCredits`,
        {
          businessUserID,
          page: nextPage,
          limit: pageSize,
          filterValues: term.trim(),
          sortingType: -1,
          sortingField: "_id",
          searchParameterList: [
            "transactionDateTime",
            "creditsPurchased",
            "transactionDescription",
            "creditsUsed",
            "creditsBalance",
            "transactionDateTimeString",
          ],
        },
        { headers: { Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}` } }
      );
      const result = response.data?.Result_Output || {};
      setLedger(result.docs || []);
      setTotal(numberOrZero(result.totalDocs));
    } catch (error) {
      setLedger([]);
      setTotal(0);
      setMessage(error.response?.data?.Result_Message || "Credit usage could not be loaded.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLedger(page, search);
    // Search is submitted explicitly to avoid a request on every keystroke.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [page, businessUserID]);

  const submitSearch = (event) => {
    event.preventDefault();
    setPage(1);
    fetchLedger(1, search);
  };

  const topUp = async () => {
    setMessage("");
    if (!hasCreditValue || creditError || !unitPrice || !card?.paymentMethodID) return;
    setPurchasing(true);
    try {
      const response = await axios.post(
        `${appConstants.baseURL}billing/payWithSavedCard`,
        {
          ...billingData,
          id: businessUserID,
          userID: user._id,
          businessUserID,
          planID: billingData?.currentPlanID,
          businessUserPlanID: billingData?.businessUserPlanID,
          paymentMethodID: card.paymentMethodID,
          paymentType: "Add Credit",
          noOfCredits: credits,
          planMonthlyCost: price,
        },
        { headers: { Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}` } }
      );
      if (!response.data?.Result_Status) throw new Error(response.data?.Result_Message);
      setBillingData((current) => ({
        ...current,
        credits: numberOrZero(current?.credits) + credits,
      }));
      setAmount("");
      showDraftSavedMessage(
        `${credits.toLocaleString()} credits added successfully`
      );
      setPage(1);
      fetchLedger(1, search);
    } catch (error) {
      setMessage(
        error.response?.data?.Result_Message ||
          error.message ||
          "The credit purchase could not be completed."
      );
    } finally {
      setPurchasing(false);
    }
  };

  const pageCount = Math.max(1, Math.ceil(total / pageSize));
  const billingLoading = !billingData;
  const skeletonRows = Array.from({ length: pageSize }, (_, index) => (
    <tr className="credit-usage-skeleton-row" key={`credit-skeleton-${index}`}>
      <td><Skeleton height={12} width="72%" radius={6} /></td>
      <td><Skeleton height={12} width={index % 2 ? "58%" : "76%"} radius={6} /></td>
      <td><Skeleton height={12} width={42} radius={6} /></td>
      <td><Skeleton height={12} width={38} radius={6} /></td>
      <td><Skeleton height={12} width={48} radius={6} /></td>
    </tr>
  ));
  const fillerRows = !loading && ledger.length > 0
    ? Array.from({ length: Math.max(0, pageSize - ledger.length) }, (_, index) => (
        <tr className="credit-usage-filler-row" aria-hidden="true" key={`credit-filler-${index}`}>
          <td colSpan="5">&nbsp;</td>
        </tr>
      ))
    : null;
  return (
    <>
      <PageHeading
        title="Credits"
        copy="Top up credits and review how credits have been added and used across your account."
        onBack={() => setView("overview")}
      />
      <div className="credit-management-summary">
        <section><LuCoins /><span>Available credits</span><strong>{billingLoading ? <Skeleton height={24} width={76} radius={7} /> : data.credits.toLocaleString()}</strong><small>{billingLoading ? <Skeleton height={11} width={140} radius={5} /> : "Current account balance"}</small></section>
        <section><LuCreditCard /><span>Payment method</span><strong>{billingLoading ? <Skeleton height={24} width={190} radius={7} /> : card ? `${card.brand} ending in ${card.last4}` : "Not added"}</strong><small>{billingLoading ? <Skeleton height={11} width={100} radius={5} /> : card ? `Expires ${card.expiry}` : "A saved card is required to top up"}</small></section>
      </div>
      <section className="credit-topup-card">
        <div><h3>Top up credits</h3><p>Credits can be used for SMS, ABN checks and letters of demand.</p></div>
        <div className="credit-topup-fields">
          <label className={creditError ? "has-error" : ""}><span>Number of credits <b>*</b></span><input type="number" min="1" max="2000" step="1" value={amount} onChange={(event) => { setAmount(event.target.value); setMessage(""); }} placeholder="Enter credits" aria-invalid={Boolean(creditError)} aria-describedby={creditError ? "credit-amount-error" : "credit-amount-helper"} /><small id={creditError ? "credit-amount-error" : "credit-amount-helper"}>{creditError || "Minimum 1, maximum 2,000 credits."}</small></label>
          <label><span>Total (AUD)</span><input value={`$${price.toFixed(2)}`} readOnly aria-label="Total in Australian dollars" /><small>{unitPrice ? `$${unitPrice.toFixed(2)} per credit` : "Price is based on your current plan."}</small></label>
        </div>
        {message && <p className="credit-management-message" role="status">{message}</p>}
        <footer>
          {!card && <button type="button" className="credit-payment-link" onClick={() => setView("billing")}>Add payment method</button>}
          <ActionButton
            className="credit-topup-submit"
            onClick={topUp}
            disabled={!hasCreditValue || Boolean(creditError) || purchasing || !unitPrice || !card?.paymentMethodID}
            aria-busy={purchasing || undefined}
          >
            {purchasing ? (
              <CircularProgress
                className="credit-topup-spinner"
                color="inherit"
                size={16}
                thickness={5}
                aria-hidden="true"
              />
            ) : (
              <span>Top up credits</span>
            )}
          </ActionButton>
        </footer>
      </section>
      <section className="credit-usage-card">
        <div className="credit-usage-heading"><div><h3>Credit usage</h3><p>Review purchases, usage and the balance after each transaction.</p></div><div className="credit-usage-search"><LuSearch aria-hidden="true" /><ClearableSearchInput aria-label="Search credit usage" value={search} onChange={(event) => { const nextSearch = event.target.value; setSearch(nextSearch); if (!nextSearch) { setPage(1); fetchLedger(1, ""); } }} onKeyDown={(event) => { if (event.key === "Enter") submitSearch(event); }} placeholder="Search credit usage" /></div></div>
        <div className="credit-usage-table-wrap"><table className="credit-usage-table"><thead><tr><th>Date</th><th>Description</th><th>Purchased</th><th>Used</th><th>Balance</th></tr></thead><tbody aria-busy={loading}>
          {loading ? skeletonRows : ledger.length ? <>{ledger.map((row, index) => <tr key={row._id || `${row.transactionDateTimeString}-${index}`}><td>{row.transactionDateTimeString || row.transactionDateTime || "â€”"}</td><td>{row.transactionDescription || "Credit transaction"}</td><td className="credit-positive">{numberOrZero(row.creditsPurchased) ? `+${numberOrZero(row.creditsPurchased).toLocaleString()}` : "â€”"}</td><td>{numberOrZero(row.creditsUsed) ? `-${numberOrZero(row.creditsUsed).toLocaleString()}` : "â€”"}</td><td><strong>{numberOrZero(row.creditsBalance).toLocaleString()}</strong></td></tr>)}{fillerRows}</> : <tr className="credit-usage-empty-row"><td colSpan="5">No credit usage is available for this account.</td></tr>}
        </tbody></table></div>
        <footer><span>Showing {ledger.length} of {total} transactions</span><div><button type="button" aria-label="Previous page" disabled={page <= 1 || loading} onClick={() => setPage((current) => current - 1)}><LuChevronLeft /></button><span>Page {page} of {pageCount}</span><button type="button" aria-label="Next page" disabled={page >= pageCount || loading} onClick={() => setPage((current) => current + 1)}><LuChevronRight /></button></div></footer>
      </section>
    </>
  );
}

function InvoiceHistory({ data, setView }) {
  return (
    <>
      <PageHeading title="Invoice history" copy="View and download invoices for your Invoice & Recover subscription. Amounts are shown in AUD." onBack={() => setView("overview")} />
      <div className="plan-summary-grid">
        <section><span>Current price</span><strong>{data.price ? `$${data.price.toFixed(2)}` : "Free"}</strong><small>Monthly subscription</small></section>
        <section><span>Billing frequency</span><strong>{data.period || "Monthly"}</strong><small>{data.plan} plan</small></section>
        <section><span>Currency</span><strong>AUD</strong><small>Australian dollars</small></section>
      </div>
      <div className="plan-history-toolbar"><strong>0 invoices</strong><div><select aria-label="Invoice period"><option>All periods</option></select></div></div>
      <div className="plan-history-table-wrap">
        <table className="plan-history-table"><thead><tr><th>Invoice number</th><th>Billing period</th><th>Issue date</th><th>Amount (AUD)</th><th>Status</th><th>Action</th></tr></thead>
          <tbody><tr><td colSpan="6">No subscription invoices are available for this account.</td></tr></tbody>
        </table>
        <footer><span>Showing 0 invoices</span><div><button type="button" disabled><LuChevronLeft /></button><button type="button" disabled><LuChevronRight /></button></div></footer>
      </div>
    </>
  );
}

function CardVisual({ card }) {
  return <div className="plan-card-visual"><b>invoice &amp; recover</b><i /><strong>••••&nbsp; ••••&nbsp; ••••&nbsp; {card.last4}</strong><span>CARDHOLDER<br /><b>{card.name.toUpperCase()}</b></span><span>EXPIRES<br /><b>{card.expiry}</b></span></div>;
}

function BillingDetails({ data, card, setView }) {
  return (
    <>
      <PageHeading title="Billing details" copy="Manage the billing contact and payment method for your Invoice & Recover subscription." onBack={() => setView("overview")} />
      <div className="billing-status-grid">
        <section><small>CURRENT PLAN</small><strong>{data.plan} · ${data.price} AUD / month</strong><span>{data.period || "Monthly"} subscription</span></section>
        <section><small>PAYMENT METHOD</small><strong>{card ? `${card.brand} ending in ${card.last4}` : "No payment method added"}</strong><span>{card ? `${card.defaultCard === "Yes" ? "Default payment method · " : ""}Expires ${card.expiry}` : "Add a method to keep your subscription active"}</span></section>
      </div>
      <section className="billing-contact-card"><h3>Billing contact</h3><p>Used on subscription invoices and billing correspondence.</p><div className="billing-form-grid"><label><span className="billing-required-label"><b aria-hidden="true">*</b>Contact name</span><input defaultValue={data.organisation} required /><small>Name shown on your subscription invoices.</small></label><label><span className="billing-required-label"><b aria-hidden="true">*</b>Billing email</span><input type="email" defaultValue={data.email} required /><small>Email address for billing correspondence.</small></label><label><span className="billing-required-label"><b aria-hidden="true">*</b>Billing address</span><input placeholder="Enter billing address" defaultValue={data.address} required /><small>Address used on subscription invoices.</small></label><label>ABN<input inputMode="numeric" placeholder="Enter Australian Business Number" defaultValue={data.abn} /><small>Optional unless required for your billing records.</small></label></div></section>
      <section className="billing-payment-card"><div><h3>Payment method</h3><p>{card ? "Your subscription is billed monthly using this saved payment method." : "Add a payment method to enable subscription billing and help keep your plan active."}</p></div>{card ? <div className="billing-saved-card"><CardVisual card={card} /><div><h3>Saved payment method</h3><strong>Visa ending in {card.last4} <em>Default</em></strong><p>Expires {card.expiry}</p><p>Used for your Invoice &amp; Recover subscription.</p></div></div> : <strong>No payment method added</strong>}<ActionButton onClick={() => setView("add-card")}>{card ? "Change payment method" : "Add payment method"}</ActionButton></section>
      <div className="billing-footer-actions"><ActionButton secondary onClick={() => setView("overview")}>Cancel</ActionButton><ActionButton>Save changes</ActionButton></div>
    </>
  );
}

function AddCard({ setView, onSave }) {
  const [form, setForm] = useState({ name: "", number: "", expiry: "", cvv: "" });
  const [submitted, setSubmitted] = useState(false);
  const update = (key, value) => setForm((old) => ({ ...old, [key]: value }));
  const valid = form.name.trim() && form.number.replace(/\D/g, "").length >= 12 && /^\d{2}\s?\/\s?\d{2}$/.test(form.expiry) && /^\d{3,4}$/.test(form.cvv);
  const save = () => { setSubmitted(true); if (valid) onSave({ name: form.name.trim(), last4: form.number.replace(/\D/g, "").slice(-4), expiry: form.expiry.replace(/\s/g, "") }); };
  return (
    <>
      <PageHeading title="Add payment method" copy="Add a payment method for your Invoice & Recover subscription. Your saved method will be used for subscription billing." onBack={() => setView("billing")} />
      <section className="add-card-panel"><h3>Card details</h3><div className="billing-form-grid"><label>Name on card <b>*</b><input className={submitted && !form.name.trim() ? "error" : ""} value={form.name} onChange={(e) => update("name", e.target.value)} placeholder="Enter name as shown on card" /><small>{submitted && !form.name.trim() ? "Enter the name shown on the card." : "Use the name printed on the payment card."}</small></label><label>Card number <b>*</b><input className={submitted && form.number.replace(/\D/g, "").length < 12 ? "error" : ""} inputMode="numeric" value={form.number} onChange={(e) => update("number", e.target.value.replace(/[^\d ]/g, "").slice(0, 19))} placeholder="Enter card number" /><small>{submitted && form.number.replace(/\D/g, "").length < 12 ? "Enter a valid card number." : " "}</small></label><label>Expiry date <b>*</b><input className={submitted && !/^\d{2}\s?\/\s?\d{2}$/.test(form.expiry) ? "error" : ""} inputMode="numeric" value={form.expiry} onChange={(e) => update("expiry", e.target.value.replace(/[^\d/ ]/g, "").slice(0, 5))} placeholder="MM / YY" /><small>{submitted && !/^\d{2}\s?\/\s?\d{2}$/.test(form.expiry) ? "Enter an expiry date in MM / YY format." : " "}</small></label><label>Security code <b>*</b><input className={submitted && !/^\d{3,4}$/.test(form.cvv) ? "error" : ""} inputMode="numeric" value={form.cvv} onChange={(e) => update("cvv", e.target.value.replace(/\D/g, "").slice(0, 4))} placeholder="Enter security code" /><small>{submitted && !/^\d{3,4}$/.test(form.cvv) ? "Enter a valid security code." : "3 or 4 digits"}</small></label></div></section>
      <p className="payment-security-copy">Use a payment method supported by your subscription provider. Your full card details should only be entered in the secure payment form.</p>
      <div className="billing-footer-actions"><ActionButton secondary onClick={() => setView("billing")}>Cancel</ActionButton><ActionButton onClick={save}>Save payment method</ActionButton></div>
    </>
  );
}

function CardSaved({ card, setView }) {
  return <><PageHeading title="Payment method saved" copy="Your payment method has been saved for your Invoice & Recover subscription. You can manage it anytime in Plan & billing." /><section className="payment-saved-panel"><CardVisual card={card} /><div><h3>Payment method details</h3><strong>Visa ending in {card.last4} <em>Default</em></strong><p>Dummy card details for design review only. Production details must come from the payment provider.</p></div></section><div className="billing-footer-actions"><ActionButton secondary onClick={() => setView("billing")}>Back</ActionButton></div></>;
}

function ManagePlan({ data, plans, setView, requestChange, manageSubscription }) {
  const planPrice = (name) => numberOrZero(plans.find((plan) => plan.name === name)?.price ?? PLAN_DATA[name]?.price);
  return (
    <>
      <PageHeading title="Manage plan" copy="Compare plans, features and pricing to find the right fit for your business." onBack={() => setView("overview")} />
      <CurrentPlan {...data} action={<ActionButton secondary onClick={manageSubscription}>Manage subscription</ActionButton>} />
      <div className="available-plan-heading"><div><h3>Available plans</h3><p>You’re on {data.plan}. Compare the available plans below.</p></div><button type="button" onClick={() => document.querySelector(".plan-comparison-table")?.scrollIntoView({ behavior: "smooth" })}>Compare plan features</button></div>
      <div className="available-plan-grid">{plans.map(({ name, price }) => { const info = PLAN_DATA[name] || { kicker: "AVAILABLE PLAN", copy: "Subscription plan", features: [] }; return <section key={name} className={name === data.plan ? "current" : ""}><h3>{name}</h3><small>{name === data.plan ? "CURRENT PLAN" : info.kicker}</small><p>{info.copy}</p><strong>{price ? `$${price} AUD / month` : "Free"}</strong><hr /><small>INCLUDED</small><ul>{info.features.map((item) => <li key={item}><LuCheck /> {item}</li>)}</ul><ActionButton secondary={name !== "Platinum"} disabled={name === data.plan} onClick={() => name !== data.plan && requestChange(name)}>{name === data.plan ? "Current plan" : name === "Platinum" ? "Contact sales" : `Switch to ${name}`}</ActionButton></section>; })}</div>
      <section className="plan-comparison"><h3>Compare plan features</h3><p>See what changes before you switch plans. Prices are in AUD and billed monthly where applicable.</p><div><table className="plan-comparison-table"><thead><tr><th>Feature</th><th>Silver{data.plan === "Silver" ? " · Current" : ""}</th><th>Gold{data.plan === "Gold" ? " · Current" : ""}</th><th>Platinum{data.plan === "Platinum" ? " · Current" : ""}</th></tr></thead><tbody><tr><td>Monthly price</td><td>${planPrice("Silver")}</td><td>${planPrice("Gold")}</td><td>${planPrice("Platinum")}</td></tr><tr><td>Invoices &amp; quotations</td><td>10 / month</td><td>100 / month</td><td>Unlimited</td></tr><tr><td>Reporting &amp; reminders</td><td>Basic reminders</td><td>Reports + reminder logs</td><td>Advanced support</td></tr><tr><td>Debt collection</td><td>View only</td><td>Full actions</td><td>Priority escalation</td></tr><tr><td>RCTI &amp; expenses</td><td>Not included</td><td>Included</td><td>Included</td></tr><tr><td>Support</td><td>Standard</td><td>Standard</td><td>Dedicated specialist</td></tr></tbody></table></div></section>
    </>
  );
}

function PlanModal({ data, plans, target, result, subscriptionStep, onClose, onConfirm, onSubscriptionStep, setView }) {
  if (!target && !result && !subscriptionStep) return null;
  const subscriptionConfirm = subscriptionStep === "confirm";
  const subscriptionManage = subscriptionStep === "manage";
  const title = subscriptionConfirm ? "Cancel your subscription?" : subscriptionManage ? "Manage subscription" : result ? "Plan change submitted" : "Review your plan change";
  const subscriptionClass = subscriptionStep
    ? ` subscription-modal subscription-modal--${subscriptionStep}`
    : "";
  const SubscriptionIcon = subscriptionConfirm
    ? LuTriangleAlert
    : LuCircleAlert;

  return (
    <div
      className="plan-modal-backdrop"
      role="presentation"
      onMouseDown={(event) => event.target === event.currentTarget && onClose()}
    >
      <section
        className={`plan-modal${subscriptionClass}`}
        role="dialog"
        aria-modal="true"
        aria-labelledby="plan-modal-title"
      >
        <header>
          {subscriptionStep && (
            <SubscriptionIcon className="plan-modal-title-icon" aria-hidden="true" />
          )}
          <h3 id="plan-modal-title">{title}</h3>
          <button type="button" onClick={onClose} aria-label="Close">
            <LuX />
          </button>
        </header>
        <div>
          {subscriptionManage ? (
            <p>
              Current plan: {data.plan} · ${data.price} AUD / month.
              <br />
              Review your subscription before making changes. Cancelling opens a
              separate confirmation step.
            </p>
          ) : subscriptionConfirm ? (
            <p>
              Your {data.plan} subscription will be cancelled according to your
              subscription terms. The effective date and any changes to access or
              billing depend on those terms.
            </p>
          ) : result ? (
            <>
              <p>
                Your request to change plans has been submitted. We’ll reflect the
                selected plan in your account once the change has been processed.
              </p>
              <p>
                You can review your current plan, pricing and available features in
                Plan &amp; billing. Any billing adjustment will follow your
                subscription terms.
              </p>
            </>
          ) : (
            <>
              <p>
                You’re switching from {data.plan} to {target}. Your plan features and
                limits will change. Review the details before confirming.
              </p>
              <p>
                Monthly price: {numberOrZero(plans.find((plan) => plan.name === target)?.price) ? `$${numberOrZero(plans.find((plan) => plan.name === target)?.price)} AUD/month` : "Free"} (currently ${data.price} AUD/month on {data.plan}). Your effective date and any pro-rata adjustment depend on your subscription terms and must be confirmed before the change is applied.
              </p>
            </>
          )}
        </div>
        <footer>
          {subscriptionManage ? (
            <>
              <ActionButton secondary onClick={onClose}>Close</ActionButton>
              <button type="button" className="plan-cancel-link" onClick={() => onSubscriptionStep("confirm")}>Cancel subscription</button>
            </>
          ) : subscriptionConfirm ? (
            <>
              <ActionButton secondary onClick={onClose}>Keep plan</ActionButton>
              <button type="button" className="plan-cancel-link" onClick={onClose}>Confirm cancellation</button>
            </>
          ) : result ? (
            <>
              <ActionButton secondary onClick={onClose}>Back to plan</ActionButton>
              <ActionButton onClick={() => { onClose(); setView("manage"); }}>View plan details</ActionButton>
            </>
          ) : (
            <>
              <ActionButton secondary onClick={onClose}>Keep current plan</ActionButton>
              <ActionButton onClick={onConfirm}>Confirm plan change</ActionButton>
            </>
          )}
        </footer>
      </section>
    </div>
  );
}

export default function PlanBillingSettings({ user, initialView = "overview" }) {
  const [view, setView] = useState(initialView);
  const [card, setCard] = useState(null);
  const [billingData, setBillingData] = useState(null);
  const [availablePlans, setAvailablePlans] = useState([]);
  const [targetPlan, setTargetPlan] = useState("");
  const [resultOpen, setResultOpen] = useState(false);
  const [subscriptionStep, setSubscriptionStep] = useState("");
  useEffect(() => {
    setView(initialView);
  }, [initialView]);
  useEffect(() => {
    const businessUserID = user.businessUserID;
    if (!businessUserID) return undefined;
    let active = true;
    const request = (path, body) => axios({
      method: body ? "post" : "get",
      url: `${appConstants.baseURL}${path}`,
      data: body,
      headers: { Authorization: `Bearer ${sessionStorage.getItem("TOKEN")}` },
    });
    Promise.allSettled([
      request("billing/fetchCurrentPlanDetails", { businessUserID }),
      request("billing/fetchSubscriptionPlans"),
      request("login/fetchCredit", { businessUserID }),
      request("billing/fetchcards", { businessUserID }),
      request("invoice/fetchInvoices", { businessUserID, _id: [], invoiceStatus: "All", activityStatus: "All", durationDate: "", fromDate: "", toDate: "", page: 1, limit: 1, registerView: true, filterValues: "", sendEmailFlag: false, sortingType: "", sortingField: "", searchParameterList: [] }),
      request("quotation/fetchQuotations", { businessUserID, quotationStatus: "All", activityStatus: "All", customer: "All customers", durationDate: "", fromDate: "", toDate: "", page: 1, limit: 1, filterValues: "", sortingType: "", sortingField: "", searchParameterList: [] }),
    ]).then(([planResult, plansResult, creditResult, cardsResult, invoicesResult, quotesResult]) => {
      if (!active) return;
      const planOutput = planResult.status === "fulfilled" ? planResult.value.data?.Result_Output : null;
      const currentPlan = planOutput?.currentPlanDetails?.[0] || {};
      const plans = plansResult.status === "fulfilled" ? plansResult.value.data?.Result_Output || [] : [];
      const normalizedPlans = plans.map((plan) => ({
        id: plan._id,
        name: plan.planName,
        price: numberOrZero(plan.planMonthlyCost),
        period: plan.planPeriod,
      }));
      const matchedPlan = normalizedPlans.find((plan) => plan.id === currentPlan.currentPlanID || plan.name === currentPlan.planName);
      const credit = creditResult.status === "fulfilled" ? creditResult.value.data?.Result_Output?.[0]?.availableCredits : undefined;
      const cards = cardsResult.status === "fulfilled" ? cardsResult.value.data?.Result_Output || [] : [];
      const invoices = invoicesResult.status === "fulfilled" ? invoicesResult.value.data?.Result_Output : {};
      const quotes = quotesResult.status === "fulfilled" ? quotesResult.value.data?.Result_Output : {};
      const defaultCard = cards.find((item) => item.defaultCard === "Yes") || cards[0];
      setAvailablePlans(normalizedPlans);
      setBillingData({
        ...currentPlan,
        creditUnitPrice: numberOrZero(planOutput?.credit?.[0]?.featureCount),
        price: matchedPlan?.price,
        period: matchedPlan?.period || currentPlan.planPeriod,
        credits: credit,
        invoices: invoices?.totalDocs ?? invoices?.docs?.length,
        quotes: quotes?.totalDocs ?? quotes?.docs?.length,
      });
      setCard(defaultCard ? {
        ...defaultCard,
        last4: defaultCard.cardNumber,
        brand: defaultCard.brand ? `${defaultCard.brand.charAt(0).toUpperCase()}${defaultCard.brand.slice(1)}` : "Card",
        name: user.fullName || user.companyName || user.businessName || "Account holder",
        expiry: `${String(defaultCard.expiryMonth).padStart(2, "0")}/${String(defaultCard.expiryYear).slice(-2)}`,
      } : null);
    });
    return () => { active = false; };
  }, [user.businessUserID, user.businessName, user.companyName, user.fullName]);

  const data = useMemo(() => {
    const plan = billingData?.planName || user.planName || user.subscriptionPlan || "Silver";
    const planLimit = PLAN_LIMITS[plan] ?? (plan === "Platinum" ? null : 0);
    return ({
    plan,
    price: numberOrZero(billingData?.price ?? user.planPrice ?? PLAN_DATA[plan]?.price),
    period: billingData?.period || "Monthly",
    invoices: numberOrZero(billingData?.invoices ?? user.invoiceCount ?? user.usedInvoices),
    invoiceLimit: user.invoiceLimit == null ? planLimit : numberOrZero(user.invoiceLimit),
    quotes: numberOrZero(billingData?.quotes ?? user.quotationCount ?? user.usedQuotations),
    quoteLimit: user.quoteLimit == null ? planLimit : numberOrZero(user.quoteLimit),
    credits: numberOrZero(billingData?.credits ?? user.availableCredits ?? user.credits ?? user.creditBalance),
    organisation: user.companyName || user.businessName || user.fullName || "Account holder",
    email: user.emailAddress || user.email || "",
    address: user.streetAddress || "",
    abn: formatAbn(user.abnNumber),
  });
  }, [billingData, user]);
  const plans = availablePlans.length ? availablePlans : Object.entries(PLAN_DATA).map(([name, info]) => ({ name, price: info.price }));
  const content = view === "history" ? <InvoiceHistory data={data} setView={setView} /> : view === "billing" ? <BillingDetails data={data} card={card} setView={setView} /> : view === "credits" ? <CreditManagement data={data} card={card} billingData={billingData} setBillingData={setBillingData} setView={setView} user={user} /> : view === "add-card" ? <AddCard setView={setView} onSave={(next) => { setCard(next); setView("card-saved"); }} /> : view === "card-saved" ? <CardSaved card={card} setView={setView} /> : view === "manage" ? <ManagePlan data={data} plans={plans} setView={setView} requestChange={setTargetPlan} manageSubscription={() => setSubscriptionStep("manage")} /> : <Overview data={data} setView={setView} />;
  return <div className="plan-billing-workspace">{content}<PlanModal data={data} plans={plans} target={targetPlan} result={resultOpen} subscriptionStep={subscriptionStep} onSubscriptionStep={setSubscriptionStep} setView={setView} onClose={() => { setTargetPlan(""); setResultOpen(false); setSubscriptionStep(""); }} onConfirm={() => { setTargetPlan(""); setResultOpen(true); }} /></div>;
}
